import React, { Component } from 'react';
import './book-detail.css';
import Sdk from 'api.digitalpages.module.sdk.api';
import AuthorService from '../../services/AuthorService';
import ReadingListService from '../../services/ReadingListService';
import { BookDetail, ReadingListModal, SummaryModal } from '../../components';
import StarRatingModal from '../../components/StarRatingModal';

import {
  HeaderShelf,
  SidebarRoundedIcons,
  Footer,
  Preloader
} from '../../components';
import { UsersService } from '../../services/user';

export default class ViewBookDetail extends Component {
  constructor(props) {
    super(props);

    const cached = this.loadFromCache(props.match.params.uid);
    this.state = {
      loading: !cached,
      book: cached?.book || null,
      availableEpubFormat: cached?.availableEpubFormat || false,
      availablePdfFormat: cached?.availablePdfFormat || false,
      availableAudioFormat: cached?.availableAudioFormat || false,
      availableComposerFormat: cached?.availableComposerFormat || false,
      isFavorite: cached?.isFavorite || false,
      showFullDescription: false,
      authors: cached?.authors || [],
      showReadingListModal: false,
      readingLists: [],
      selectedReadingLists: [],
      loadingReadingLists: false,
      showEvaluationModal: false,
      userEvaluation: cached?.userEvaluation || null,
      evaluation: cached?.evaluation || null,
      showSummaryModal: false,
      summaries: cached?.summaries || [],
      summaryLoading: !cached,
      loadingSummary: false,
      navigateTo: {}
    };
  }

  componentDidMount() {
    const credential = Sdk.Api.authorization.credential;
    if (!credential || !credential.accessToken) {
      const currentPath = window.location.hash.replace('#', '') || window.location.pathname;
      window.localStorage.setItem('redirect_after_login', currentPath);
      this.props.history.replace('/login');
      return;
    }
    this.loadBookDetails();
    this.loadReadingLists();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.uid !== this.props.match.params.uid) {
      const cached = this.loadFromCache(this.props.match.params.uid);
      this.setState({
        loading: !cached,
        book: cached?.book || null,
        availableEpubFormat: cached?.availableEpubFormat || false,
        availablePdfFormat: cached?.availablePdfFormat || false,
        availableAudioFormat: cached?.availableAudioFormat || false,
        availableComposerFormat: cached?.availableComposerFormat || false,
        isFavorite: cached?.isFavorite || false,
        authors: cached?.authors || [],
        userEvaluation: cached?.userEvaluation || null,
        evaluation: cached?.evaluation || null,
        summaries: cached?.summaries || [],
        summaryLoading: !cached,
      });
      this.loadBookDetails();
      this.loadReadingLists();
    }
  }

  loadFromCache(uid) {
    try {
      const raw = localStorage.getItem(`book_detail_${uid}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  saveToCache(uid, data) {
    try {
      localStorage.setItem(`book_detail_${uid}`, JSON.stringify(data));
    } catch {
      // localStorage cheio ou indisponível
    }
  }

  isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  async handleLogout() {
    await UsersService.signOut();
    this.props.history.replace('/login');
  }

  loadReadingLists = async () => {
    try {
      this.setState({ loadingReadingLists: true });
      const [readingLists, bookReadingLists] = await Promise.all([
        ReadingListService.loadReadingLists(),
        this.props.match?.params?.uid ? ReadingListService.getBookReadingLists(this.props.match.params.uid) : []
      ]);
      
      // Filtrar listas que não são favoritos
      const filteredLists = readingLists.filter(list => list.name !== '###InternalFavorites###');
      const selectedLists = bookReadingLists.map(tag => tag.uid);
      
      this.setState({
        readingLists: filteredLists,
        selectedReadingLists: selectedLists,
        loadingReadingLists: false
      });
    } catch (error) {
      console.error('Error loading reading lists:', error);
      this.setState({ loadingReadingLists: false });
    }
  }

  loadBookDetails = async () => {
    try {
      const bookUid = this.props.match.params.uid;      
      
      // Carregar autores e dados do livro em paralelo
      const [authors, bookResponse] = await Promise.all([
        AuthorService.loadAuthors(),
        Sdk.Api.dynamic.bridge(`cms/v1.1/managed/content/group/uid/${bookUid}`, null, "GET")
      ]);
      
      if (bookResponse) {
        const book = this.mapBookData(bookResponse, authors);
        const formats = this.detectAvailableFormats(bookResponse.references || []);

        const cached = this.loadFromCache(bookUid);
        const primaryData = { book, authors, ...formats };

        if (!cached || !this.isEqual(cached, { ...cached, ...primaryData })) {
          this.saveToCache(bookUid, { ...cached, ...primaryData });
        }

        this.setState({
          book,
          authors,
          ...formats,
          loading: false
        });

        // Carregar favoritos, avaliações e sumários em background
        this.loadSecondaryData(bookUid, book);
      }
    } catch (error) {
      console.error('Error loading book details:', error);
      this.setState({ loading: false });
    }
  }

  loadSecondaryData = async (bookUid, book) => {
    try {
      const [isFavorite, userEvaluation, evaluation, summaries] = await Promise.all([
        this.checkIfFavorite(bookUid),
        this.loadUserEvaluation(bookUid),
        this.loadBookEvaluation(bookUid),
        this.loadSummaries(book)
      ]);

      const cached = this.loadFromCache(bookUid);
      const secondaryData = { isFavorite, userEvaluation, evaluation, summaries, summaryLoading: false };

      if (!cached || !this.isEqual(cached, { ...cached, ...secondaryData })) {
        this.saveToCache(bookUid, { ...cached, ...secondaryData });
      }

      this.setState(secondaryData);
    } catch (error) {
      console.error('Error loading secondary data:', error);
    }
  }

  loadSummaries = async (book) => {
    let pdfSummary = [];
    let epubSummary = [];

    if (book.references && Array.isArray(book.references)) {
      for (const reference of book.references) {
        if (reference.mime_type === "application/epub+zip") {
          epubSummary = await this.getEpubSummary(reference.uid);
        } else if (reference.mime_type === "digitalpages/pdf") {
          pdfSummary = await this.getPdfSummary(reference.uid);
        }
      }
    }

    // console.log('PDF Summary:', pdfSummary);
    // console.log('EPUB Summary:', epubSummary);

    return [...pdfSummary, ...epubSummary];
  }

  getEpubSummary = async (uid) => {
    try {
      const url = `cms/v1.1/managed/content/reference/uid/${uid}/default/target`;
      const response = await Sdk.Api.dynamic.bridgeUrl(url, null);
      
      const contentToc = await fetch(response);
      const contentTocUrl = contentToc.url.replace("content.opf?", "toc.ncx?");
      
      const manifest = await fetch(contentTocUrl).then(p => p.text());
      
      const parser = new DOMParser();
      const xml = parser.parseFromString(manifest, "text/xml");
      const navPoints = xml.getElementsByTagName("navPoint");
      
      return Array.from(navPoints).map(navPoint => {
        const content = navPoint.getElementsByTagName('content')[0];
        const navLink = content?.getAttribute('src');
        
        return {
          type: "epub",
          uid: navPoint.id?.toString(),
          title: navPoint.textContent?.trim().replace(/\s+/g, ' ').replace(/(\r\n|\n|\r)/gm, ""),
          navLink
        };
      });
    } catch (error) {
      console.error('Error getting EPUB summary:', error);
      return [];
    }
  }

  getPdfSummary = async (uid) => {
    try {
      // var content = await Sdk.Api.dynamic.bridgeUrl(`cms/v1.1/managed/content/reference/uid/${reference}/content`, null);
      const url = `cms/v1.1/managed/content/reference/uid/${uid}/content`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");

      const newurl = new URL(response.url);
      const params = new URLSearchParams(newurl.search);
      const customOptionsEncoded = params.get("custom_options");

      const customOptions = JSON.parse(
        atob(decodeURIComponent(customOptionsEncoded))
      );

      const storagePath = customOptions.activity.url.replace(`content`, `content/path/summary.json`);
      const path = `${storagePath}&api_env=${Sdk.Api.enviroment}&project_key=${Sdk.Api.projectKey}&access_token=${Sdk.Api.authorization.credential.accessToken}`;
      // console.log(path);

      const summaryRequest = await fetch(path);
      const summary = await summaryRequest.json();
      
      return summary.map(item => ({
        type: "pdf",
        uid: item.page?.toString(),
        title: item.label,
        navLink: JSON.stringify({ page: item.page })
      }));
    } catch (error) {
      console.error('Error getting PDF summary:', error);
      return [];
    }
  }

  handleSummary = () => {
    this.setState({ showSummaryModal: true });
  }

  handleCloseSummaryModal = () => {
    this.setState({ showSummaryModal: false });
  }

  handleNavigation = (summary, type) => {
    // console.log(summary);
    // console.log(type);
    const {book} = this.state

    var ref = '';

    switch(type) {
      case 'pdf':
        ref = this.state.book.references.find(ref => ref.mime_type === 'digitalpages/pdf');
        break;      
      case 'epub':
        ref = this.state.book.references.find(ref => ref.mime_type === 'application/epub+zip');
        break;
      case 'digitalpages/audiobook':
        ref = this.state.book.references.find(ref => ref.mime_type === 'digitalpages/audiobook')
        break;
      default:
        console.log('type not defined')
    }
    // this.setState({
    //   navigateTo: summary,
    // });
    if(!ref)
      return;
    this.props.history.push({
      pathname: `/livro/${book.uid}/${ref.uid}`,
      state: { navigateTo: summary }
    });

  }

  decodeUnicode (str){
    // Going backwards: from bytestream, to percent-encoding, to original string.
    return decodeURIComponent(atob(str).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  getParameterByName(name, url) {
    name = name.replace(/[\[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    var results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }


  mapBookData = (serverData, authors = []) => {
    // Primeiro tentar buscar autor nas properties
    let author = serverData.properties?.find(p => 
      p.name === "Autor" || 
      p.name === "autor" || 
      p.name === "Author" || 
      p.name === "AUTOR"
    )?.value;
    
    // Se não encontrou nas properties, usar a lógica baseada em entities
    if (!author && serverData.entities && authors.length) {
      author = AuthorService.updateContentAuthor(serverData, authors);
    }
    
    // Fallbacks adicionais
    if (!author || author === 'Autor não disponível') {
      author = serverData.author || 
               serverData.properties?.find(p => p.name.toLowerCase().includes('autor'))?.value ||
               'Autor não disponível';
    }
    
    return {
      uid: serverData.uid,
      title: serverData.name || 'Título não disponível',
      author: author,
      description: serverData.properties?.find(p => p.name === "Descrição" || p.name === "descrição")?.value || serverData.description,
      edition: serverData.properties?.find(p => p.name === "Número da edição")?.value || serverData.properties?.find(p => p.name === "Edição")?.value,
      evaluation: serverData.properties?.find(p => p.name === "Avaliação")?.value || '0',
      readingTime: serverData.properties?.find(p => p.name === "Tempo de leitura")?.value || '30 min',
      image: this.getImageUrl(serverData.uid),
      references: serverData.references || [],
      entities: serverData.entities || []
    };
  }

  detectAvailableFormats = (references) => {
    const formats = {
      availableEpubFormat: false,
      availablePdfFormat: false,
      availableAudioFormat: false,
      availableComposerFormat: false
    };

    references.forEach(ref => {
      if (ref.mime_type === 'application/epub+zip') {
        formats.availableEpubFormat = true;
      // } else if (ref.mime_type === 'digitalpages/richpack' || ref.mime_type === 'application/pdf') {
      } else if (ref.mime_type === "digitalpages/pdf") {
        formats.availablePdfFormat = true;
      } else if (ref.mime_type === 'digitalpages/audiobook') {
        formats.availableAudioFormat = true;
      } else if (ref.mime_type === 'digitalpages/composer') {
        formats.availableComposerFormat = true;
      }
    });

    return formats;
  }

  getImageUrl = (uid) => {
    return `${Sdk.Api.domain}/cms/v1.1/managed/content/group/uid/${uid}/default/thumb?image_options=width:200&api_env=${Sdk.Api.enviroment}&project_key=${Sdk.Api.projectKey}&access_token=${Sdk.Api.authorization.credential.accessToken}`;
  }

  handleReadEpub = () => {
    const epubRef = this.state.book.references.find(ref => ref.mime_type === 'application/epub+zip');
    if (epubRef) {
      this.props.history.push(`/livro/${this.state.book.uid}/${epubRef.uid}`);
    }
  }

  handleOpenComposer = () => {
    const composerRef = this.state.book.references.find(ref => ref.mime_type === 'digitalpages/composer');
    if (composerRef) {
      this.props.history.push(`/livro/${this.state.book.uid}/${composerRef.uid}`);
    }
  }


  handleReadPdf = () => {
    // console.log("pdf click")
    const pdfRef = this.state.book.references.find(ref => ref.mime_type === 'digitalpages/pdf');
    if (pdfRef) {
      this.props.history.push(`/livro/${this.state.book.uid}/${pdfRef.uid}`);
    }
  }

  handleOpenAudiobook = () => {
    const audioRef = this.state.book.references.find(ref => ref.mime_type === 'digitalpages/audiobook');
    if (audioRef) {
      this.props.history.push(`/livro/${this.state.book.uid}/${audioRef.uid}`);
    }
  }

  handleEvaluation = () => {
    this.setState({ showEvaluationModal: true });
  }

  handleCloseEvaluationModal = () => {
    this.setState({ showEvaluationModal: false });
  }

  handleRate = async (rating) => {
    try {
      const { book } = this.state;
      if(!this.state.userEvaluation) 
        await this.saveUserEvaluation(book.uid, rating);
      else {
        await this.updateUserEvaluation(this.state.userEvaluation.uid, rating);
      }
      this.setState({ userEvaluation: rating });
    } catch (error) {
      console.error('Error saving evaluation:', error);
    }
  }

  handleRemoveRating = async () => {
    try {
      const { userEvaluation } = this.state;
      await this.removeUserEvaluation(userEvaluation.uid);
      this.setState({ userEvaluation: null });
    } catch (error) {
      console.error('Error removing evaluation:', error);
    }
  }

  saveUserEvaluation = async (bookUid, rating) => {
    const url = `cms/v1.1/evaluation/managedContent/uid/${bookUid}/score`;
    await Sdk.Api.dynamic.bridge(url, { note: rating }, 'POST');
    this.loadBookDetails();
  }

  updateUserEvaluation = async (bookUid, rating) => {
    const url = `cms/v1.1/evaluation/uid/${bookUid}`;
    await Sdk.Api.dynamic.bridge(url, { note: rating }, 'PUT');
    this.loadBookDetails();
  }

  removeUserEvaluation = async (bookUid) => {
    const url = `cms/v1.1/evaluation/uid/${bookUid}`;
    await Sdk.Api.dynamic.bridge(url, null, "DELETE");
  }

  loadUserEvaluation = async (bookUid) => {
    try {
      const url = `cms/v1.1/evaluation/managedContent/uid/${bookUid}/score/user`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      return response[0] || null;
    } catch (error) {
      console.error('Error loading user evaluation:', error);
      return null;
    }
  }

  loadBookEvaluation = async (bookUid) => {
    try {
      const url = `cms/v1.1/evaluation/managedContent/uid/${bookUid}/score`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      return response.result || null;
    } catch (error) {
      console.error('Error loading user evaluation:', error);
      return null;
    }
  }

  handleAddToReadingList = () => {
    this.setState({ showReadingListModal: true });
  }

  handleCloseReadingListModal = () => {
    this.setState({ showReadingListModal: false });
  }

  handleToggleReadingList = async (listUid, isSelected) => {
    try {
      const { book, selectedReadingLists } = this.state;
      
      if (isSelected) {
        await ReadingListService.addBookToReadingList(book.uid, listUid);
        this.setState({
          selectedReadingLists: [...selectedReadingLists, listUid]
        });
      } else {
        await ReadingListService.removeBookFromReadingList(book.uid, listUid);
        this.setState({
          selectedReadingLists: selectedReadingLists.filter(id => id !== listUid)
        });
      }
    } catch (error) {
      console.error('Error toggling reading list:', error);
    }
  }

  handleCreateReadingList = async (name, description) => {
    try {
      const newList = await ReadingListService.createReadingList(name, description);
      const { readingLists } = this.state;
      
      this.setState({
        readingLists: [...readingLists, {
          uid: newList.uid,
          name: newList.name || name,
          description: newList.description || description
        }]
      });
    } catch (error) {
      console.error('Error creating reading list:', error);
    }
  }

  handleFavorite = async () => {
    try {
      const { book, isFavorite } = this.state;
      
      if (isFavorite) {
        // Remover dos favoritos
        await this.removeFromFavorites(book);
        this.setState({ isFavorite: false });
      } else {
        // Adicionar aos favoritos
        await this.addToFavorites(book);
        this.setState({ isFavorite: true });
      }
    } catch (error) {
      console.error('Error handling favorite:', error);
    }
  }

  addToFavorites = async (book) => {
    // 1. Buscar connector UserData
    // const connectors = Sdk.Api.authorization.user.directory.connectors;
    const connectors = Sdk.Api.authorization.activeProject?.connectors;
    const connectorUser = connectors?.find(c => 
      c.features?.includes('IConnectorUserData')
    );
    
    if (!connectorUser) {
      throw new Error('UserData connector not found');
    }

    // 2. Verificar se já existe UserData para este livro
    const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${book.uid}&context_uid=${book.uid}`;
    const existingData = await Sdk.Api.dynamic.bridge(checkUrl, null, "GET");
    
    if (existingData.result.length === 0) {
      // 3. Criar novo UserData com tag de favoritos
      const createUrl = `cms/v1.1/user/connector/uid/${connectorUser.uid}/data`;
      await Sdk.Api.dynamic.bridge(createUrl, {
        context_uid: book.uid,
        context_type: 'ManagedContent',
        target_uid: book.uid,
        target_type: 'ManagedContent',
        tags: [{ name: '###InternalFavorites###' }]
      }, "POST");
    } else {
      // 4. Atualizar UserData existente adicionando tag de favoritos
      const userData = existingData.result[0];
      const updateUrl = `cms/v1.1/user/data/uid/${userData.uid}`;
      
      if (!userData.tags) userData.tags = [];
      
      const hasFavoriteTag = userData.tags.find(t => t.name === '###InternalFavorites###');
      if (!hasFavoriteTag) {
        userData.tags.push({ name: '###InternalFavorites###' });
        await Sdk.Api.dynamic.bridge(updateUrl, userData, "PUT");
      }
    }
  }

  removeFromFavorites = async (book) => {
    // 1. Buscar UserData existente
    const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${book.uid}&context_uid=${book.uid}`;
    const existingData = await Sdk.Api.dynamic.bridge(checkUrl, null, "GET");
    
    if (existingData.result.length > 0) {
      const userData = existingData.result[0];
      const updateUrl = `cms/v1.1/user/data/uid/${userData.uid}`;
      
      // 2. Remover tag de favoritos
      if (userData.tags) {
        userData.tags = userData.tags.filter(t => t.name !== '###InternalFavorites###');
        await Sdk.Api.dynamic.bridge(updateUrl, userData, "PUT");
      }
    }
  }

  checkIfFavorite = async (bookUid) => {
    try {
      const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${bookUid}&context_uid=${bookUid}`;
      const existingData = await Sdk.Api.dynamic.bridge(checkUrl, null, "GET");
      
      if (existingData.result.length > 0) {
        const userData = existingData.result[0];
        const hasFavoriteTag = userData.tags?.find(t => t.name === '###InternalFavorites###');
        return !!hasFavoriteTag;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  }

  render() {
    const { 
      loading, 
      book, 
      availableEpubFormat, 
      availablePdfFormat, 
      availableAudioFormat, 
      availableComposerFormat,
      isFavorite,
      showReadingListModal,
      readingLists,
      selectedReadingLists,
      loadingReadingLists,
      showEvaluationModal,
      userEvaluation,
      evaluation,
      summaryLoading
    } = this.state;
    
    if (loading) {
      return (
        <Preloader/>
      );
    }

    if (!book) {
      return (
        <div className="rdp-react-template-view-book-detail">
          <div className="rdp-react-template-view-book-detail-error">
            <p>Livro não encontrado</p>
          </div>
        </div>
      );
    }

    return (
      <div className="rdp-react-template-view-book-detail">
        <div className="rdp-react-template-view-book-detail-header">
          <HeaderShelf
            onSignOut={() => { this.handleLogout() }}
            renderSearch={false}
          />
        </div>

        <div className="rdp-react-template-view-book-detail-sidebar">
          <SidebarRoundedIcons />
        </div>

        <div className="rdp-react-template-view-book-detail-main">
          <div className="rdp-react-template-view-book-detail-container">
            <BookDetail
              image={book.image}
              title={book.title}
              author={book.author}
              evaluation={evaluation}
              edition={book.edition}
              readingTime={book.readingTime}
              description={book.description}
              availableEpubFormat={availableEpubFormat}
              availablePdfFormat={availablePdfFormat}
              availableComposerFormat={availableComposerFormat}
              availableAudioFormat={availableAudioFormat}
              authenticatedUser={true} // Assumindo usuário autenticado
              userEvaluation={userEvaluation}
              isFavorite={isFavorite}
              onClickReadEpub={this.handleReadEpub}
              onClickReadPdf={this.handleReadPdf}
              onClickOpenAudiobook={this.handleOpenAudiobook}
              onClickEvaluation={this.handleEvaluation}
              onClickFavorite={this.handleFavorite}
              onClickAddToReadingList={this.handleAddToReadingList}
              onClickSummary={this.handleSummary}
              summaryLoading={summaryLoading}
              onClickOpenComposer={this.handleOpenComposer}
            />
          </div>
          <Footer/>
        </div>
        
        <ReadingListModal
          isOpen={showReadingListModal}
          onClose={this.handleCloseReadingListModal}
          readingLists={readingLists}
          selectedLists={selectedReadingLists}
          bookTitle={book?.title}
          loading={loadingReadingLists}
          onToggleList={this.handleToggleReadingList}
          onCreateList={this.handleCreateReadingList}
        />
        
        <StarRatingModal
          isOpen={showEvaluationModal}
          onClose={this.handleCloseEvaluationModal}
          onRate={this.handleRate}
          onRemoveRating={this.handleRemoveRating}
          userEvaluation={userEvaluation}
          bookTitle={book?.title}
        />
        
        <SummaryModal
          isOpen={this.state.showSummaryModal}
          onClose={this.handleCloseSummaryModal}
          summaries={this.state.summaries}
          availableEpubFormat={availableEpubFormat}
          availablePdfFormat={availablePdfFormat}
          onNavigateToContent={(summary, type) => {
            
            this.handleNavigation(summary, type);
            this.handleCloseSummaryModal();
          }}
        />
      </div>
    );
  }
}